import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { kevTicketsQuery, saveKevTickets, type KevTicketsPage } from '../../api/settingsSoftsee'
import { hrefOf } from '../../overview/href'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings > Softsee > KEV tickets (F2.7, CyFun ID.RA-08.2): pornit, OD deschide
 * un tichet pe fiecare aplicatie cu un CVE exploatat activ, cu masinile si
 * CVE-urile in el. Inainte de salvare spune cate tichete pleaca pe loc.
 */
export function KevTicketsPanel() {
  const page = useQuery(kevTicketsQuery())

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <div className="alert alert-danger">{page.error.message}</div>
  if (!page.data) return <Spinner />

  return (
    <div id="content">
      <header className="panel-heading" style={{ borderBottom: 0 }}>
        <h3>
          <strong>KEV</strong> tickets
        </h3>
        <label className="color">a vulnerability exploited in the wild gets a ticket, not a reminder</label>
      </header>
      <Body page={page.data} />
    </div>
  )
}

function Body({ page }: { page: KevTicketsPage }) {
  if (!page.ready) {
    return <div className="alert alert-warning">The KEV ticket tables are not installed yet. They come with the next database migration.</div>
  }
  if (page.company === null) {
    return <div className="alert alert-info">Pick one company in the header: KEV tickets are switched on per company.</div>
  }

  return <KevForm page={page} />
}

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

function KevForm({ page }: { page: KevTicketsPage }) {
  const client = useQueryClient()
  const [enabled, setEnabled] = useState(page.enabled)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setEnabled(page.enabled)
  }, [page.enabled])

  const dirty = enabled !== page.enabled
  const exposure = page.exposure ?? { apps: 0, machines: 0, due: 0 }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!page.canEdit || !dirty || busy) return
    setBusy(true)
    setError(null)
    try {
      const next = await saveKevTickets(enabled)
      client.setQueryData(kevTicketsQuery().queryKey, next)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The setting could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="kev-tickets-form" noValidate onSubmit={(e) => void submit(e)}>
      <p>
        Company: <strong>{page.company?.name}</strong>
      </p>
      <p>
        {exposure.apps === 0
          ? 'Right now no application here has a vulnerability on the CISA list of those exploited in the wild (KEV).'
          : `Right now ${plural(exposure.apps, 'application')} on ${plural(exposure.machines, 'machine')} carry a vulnerability exploited in the wild (CISA KEV).`}
      </p>
      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            checked={enabled}
            disabled={!page.canEdit || busy}
            aria-describedby="kev-tickets-help"
            onChange={(e) => {
              setEnabled(e.target.checked)
              setSaved(false)
            }}
          />{' '}
          Open a ticket for every application with an actively exploited vulnerability
        </label>
      </div>
      <p id="kev-tickets-help" className="text-muted" style={{ fontSize: 12 }}>
        One ticket per application, with the machines and their version and the vulnerabilities, at your urgent ticket priority; the ticket says when
        ransomware is known to use one of them. While it is open no second one opens; closed while the application is still vulnerable, a new one opens after{' '}
        {page.reopenDays} days. OD checks each company at most once an hour. This is the tracking of corrective measures the audit and NIS2 ask for (CyFun
        ID.RA-08.2).
      </p>
      {enabled && !page.enabled && exposure.due > 0 && (
        <div className="alert alert-warning">Saving this opens {plural(exposure.due, 'ticket')} now, one per application.</div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}
      {saved && !error && <div className="alert alert-success">Saved.</div>}
      {page.canEdit && (
        <button type="submit" className="btn btn-primary" disabled={!dirty || busy} aria-label="Save KEV tickets">
          {busy ? 'Saving...' : 'Save'}
        </button>
      )}
      <Recent page={page} />
    </form>
  )
}

function Recent({ page }: { page: KevTicketsPage }) {
  if (page.recent.length === 0) {
    return page.enabled ? <p className="text-muted">The first tickets open within a minute.</p> : null
  }

  return (
    <div style={{ marginTop: 16 }}>
      <p>
        <strong>Latest tickets</strong>
        {page.enabledAt && <span className="text-muted"> - on since {page.enabledAt.slice(0, 10)}</span>}
      </p>
      <div className="table-responsive">
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Application</th>
              <th>Opened</th>
              <th>At opening</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {page.recent.map((t) => (
              <tr key={t.ticket_id}>
                <td>
                  <a href={hrefOf(`easeedesk/ticket/${t.ticket_id}`) ?? '#'}>#{t.ticket_id}</a>
                </td>
                <td>{t.app}</td>
                <td>{t.opened_at.slice(0, 10)}</td>
                <td>
                  {t.cves} CVE · {plural(t.devices, 'machine')}
                </td>
                <td>{t.done ? 'closed' : 'open'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
